import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import Table from "@cloudscape-design/components/table";
import Button from "@cloudscape-design/components/button";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Select from "@cloudscape-design/components/select";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";

const STORAGE_KEY = "idp-demo-custom-instructions";

const FORMAT_OPTIONS = [
  { label: "JSON", value: "json" },
  { label: "CSV", value: "csv" },
  { label: "Text", value: "text" },
  { label: "Markdown", value: "markdown" },
  { label: "Table", value: "table" },
];

function CustomProcessingInstructions() {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState([]);
  const [editModal, setEditModal] = useState({ visible: false, instruction: null, isNew: false });
  const [deleteModal, setDeleteModal] = useState({ visible: false, instruction: null });
  const [formName, setFormName] = useState("");
  const [formPrompt, setFormPrompt] = useState("");
  const [formFormat, setFormFormat] = useState(FORMAT_OPTIONS[0]);
  const [alert, setAlert] = useState(null);

  const loadInstructions = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setInstructions(JSON.parse(saved));
    } catch (e) {
      console.error("Failed to load custom instructions:", e);
    }
  }, []);

  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  const saveInstructions = (updated) => {
    setInstructions(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const openCreate = () => {
    setFormName("");
    setFormPrompt("");
    setFormFormat(FORMAT_OPTIONS[0]);
    setEditModal({ visible: true, instruction: null, isNew: true });
  };

  const openEdit = (instruction) => {
    setFormName(instruction.name);
    setFormPrompt(instruction.prompt);
    setFormFormat(FORMAT_OPTIONS.find((o) => o.value === instruction.format) || FORMAT_OPTIONS[0]);
    setEditModal({ visible: true, instruction, isNew: false });
  };

  const handleSave = () => {
    if (!formName.trim() || !formPrompt.trim()) {
      setAlert({ type: "error", message: t("venue.config.instructions.nameAndPromptRequired") });
      return;
    }

    const entry = {
      id: editModal.isNew ? Date.now().toString() : editModal.instruction.id,
      name: formName.trim(),
      prompt: formPrompt.trim(),
      format: formFormat.value,
      formatLabel: formFormat.label,
      updatedAt: new Date().toISOString(),
    };

    let updated;
    if (editModal.isNew) {
      updated = [...instructions, entry];
    } else {
      updated = instructions.map((i) => (i.id === entry.id ? entry : i));
    }
    saveInstructions(updated);
    setEditModal({ visible: false, instruction: null, isNew: false });
    setAlert({ type: "success", message: editModal.isNew ? t("venue.config.instructions.created") : t("venue.config.instructions.updated") });
  };

  const handleDelete = () => {
    const updated = instructions.filter((i) => i.id !== deleteModal.instruction.id);
    saveInstructions(updated);
    setDeleteModal({ visible: false, instruction: null });
    setAlert({ type: "success", message: t("venue.config.instructions.deleted") });
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button variant="primary" onClick={openCreate}>
              {t("venue.config.instructions.create")}
            </Button>
          }
        >
          {t("venue.config.instructions.title")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {alert && (
          <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <Table
          columnDefinitions={[
            { id: "name", header: t("venue.config.instructions.name"), cell: (item) => item.name },
            { id: "format", header: t("venue.config.instructions.format"), cell: (item) => item.formatLabel },
            {
              id: "prompt",
              header: t("venue.config.instructions.prompt"),
              cell: (item) => (item.prompt.length > 80 ? item.prompt.substring(0, 80) + "..." : item.prompt),
            },
            {
              id: "actions",
              header: "",
              cell: (item) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => openEdit(item)} iconName="edit" variant="icon" ariaLabel={t("venue.config.instructions.edit")} />
                  <Button
                    onClick={() => setDeleteModal({ visible: true, instruction: item })}
                    iconName="remove"
                    variant="icon"
                    ariaLabel={t("venue.config.instructions.deleteAction")}
                  />
                </SpaceBetween>
              ),
            },
          ]}
          items={instructions}
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t("venue.config.instructions.empty")}</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                {t("venue.config.instructions.emptyDescription")}
              </Box>
            </Box>
          }
        />

        <Modal
          visible={editModal.visible}
          onDismiss={() => setEditModal({ visible: false, instruction: null, isNew: false })}
          header={editModal.isNew ? t("venue.config.instructions.createTitle") : t("venue.config.instructions.editTitle")}
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setEditModal({ visible: false, instruction: null, isNew: false })}>
                  {t("venue.common.cancel")}
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  {t("venue.common.save")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField label={t("venue.config.instructions.name")}>
              <Input value={formName} onChange={({ detail }) => setFormName(detail.value)} placeholder={t("venue.config.instructions.namePlaceholder")} />
            </FormField>
            <FormField label={t("venue.config.instructions.format")}>
              <Select selectedOption={formFormat} onChange={({ detail }) => setFormFormat(detail.selectedOption)} options={FORMAT_OPTIONS} />
            </FormField>
            <FormField label={t("venue.config.instructions.prompt")} description={t("venue.config.instructions.promptDescription")}>
              <Textarea
                value={formPrompt}
                onChange={({ detail }) => setFormPrompt(detail.value)}
                placeholder={t("venue.config.instructions.promptPlaceholder")}
                rows={6}
              />
            </FormField>
          </SpaceBetween>
        </Modal>

        <Modal
          visible={deleteModal.visible}
          onDismiss={() => setDeleteModal({ visible: false, instruction: null })}
          header={t("venue.config.instructions.deleteTitle")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleteModal({ visible: false, instruction: null })}>
                  {t("venue.common.cancel")}
                </Button>
                <Button variant="primary" onClick={handleDelete}>
                  {t("venue.common.delete")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {t("venue.config.instructions.deleteMessage", { name: deleteModal.instruction?.name })}
        </Modal>
      </SpaceBetween>
    </Container>
  );
}

export default CustomProcessingInstructions;
